// Helpers for the simulation inputs upload flow.
//
// SHA-256 is computed in hex (64 lowercase chars). That is the wire format the
// backend expects. The server converts to base64 internally for the S3
// x-amz-checksum-sha256 header; the frontend never has to convert.

export const REQUIRED_CSV_COLUMNS = [
  'order_number',
  'address1',
  'city',
  'postcode',
  'country',
  'address_type',
  'weight',
  'length',
  'width',
  'height',
  'total_packages',
  'signature_required',
  'inco_terms',
] as const;

export async function computeSha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export interface CsvHeaderValidationResult {
  ok: boolean;
  missing: string[];
}

const UTF8_BOM = '﻿';

export async function validateCsvHeader(file: File): Promise<CsvHeaderValidationResult> {
  // Strip UTF-8 BOM (U+FEFF) — Excel-exported CSVs prefix with one, which
  // would otherwise leave the first column as "<BOM>order_number" and fail
  // every required-column check with a misleading "missing column" message.
  let head = await file.slice(0, 64 * 1024).text();
  if (head.startsWith(UTF8_BOM)) head = head.slice(UTF8_BOM.length);
  const firstLine = head.split(/\r?\n/, 1)[0] ?? '';
  const columns = firstLine
    .split(',')
    .map((column) => column.trim().replace(/^"|"$/g, '').toLowerCase());
  const missing = REQUIRED_CSV_COLUMNS.filter((required) => !columns.includes(required));
  return { ok: missing.length === 0, missing: [...missing] };
}
